/**
 * `RichText` -> DOM.
 *
 * Sem `innerHTML` em lugar nenhum: cada nó é criado explicitamente. Enunciados vêm
 * de geradores hoje e virão de um banco de dados amanhã; tratar isso como HTML seria
 * abrir uma porta de injeção por conveniência.
 */

import katex from 'katex';
import type { Inline, RichText } from '../schema/richtext.ts';

export function renderRichText(r: RichText, tag: keyof HTMLElementTagNameMap = 'p'): HTMLElement {
  const el = document.createElement(tag);
  el.className = 'rt';
  for (const node of r) el.appendChild(renderInline(node));
  return el;
}

/** Versão em linha, para caber dentro de um botão ou de uma célula. */
export function renderRichInline(r: RichText): DocumentFragment {
  const frag = document.createDocumentFragment();
  for (const node of r) frag.appendChild(renderInline(node));
  return frag;
}

function renderInline(node: Inline): Node {
  switch (node.t) {
    case 'text':
      return document.createTextNode(node.s);

    case 'strong': {
      const el = document.createElement('strong');
      el.textContent = node.s;
      return el;
    }

    case 'em': {
      const el = document.createElement('em');
      el.textContent = node.s;
      return el;
    }

    case 'math': {
      const el = document.createElement('span');
      el.className = 'math';
      try {
        katex.render(node.tex, el, { throwOnError: false, displayMode: false });
      } catch {
        // Fallback honesto: mostra o LaTeX cru em vez de sumir com o conteúdo.
        el.textContent = node.tex;
        el.classList.add('math-fallback');
      }
      return el;
    }

    case 'unit': {
      // `white-space: nowrap` para o valor nunca se separar da unidade na quebra.
      const el = document.createElement('span');
      el.className = 'unit';
      el.textContent = `${node.value} ${node.unit}`;
      return el;
    }

    case 'ref': {
      const el = document.createElement('span');
      el.className = 'ref';
      el.textContent = node.label;
      return el;
    }

    case 'br':
      return document.createElement('br');
  }
}
